import React from 'react';
import { globalSearch } from '../../../../../helpers/search-helper';

const EMPTY_RESULTS = {
    tickets: [],
    customers: [],
    projects: [],
    users: [],
};

export const useGlobalSearch = ({
    token,
    initialQuery = '',
    initialFilter = 'all',
    limit = 10,
    debounceMs = 400,
} = {}) => {
    const [query, setQuery] = React.useState(() => String(initialQuery || ''));
    const [activeFilter, setActiveFilter] = React.useState(() => {
        const normalized = String(initialFilter || 'all').toLowerCase();
        return ['all', 'ticket', 'customer', 'project', 'user'].includes(normalized) ? normalized : 'all';
    });
    const [results, setResults] = React.useState(EMPTY_RESULTS);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        setQuery(String(initialQuery || ''));
    }, [initialQuery]);

    React.useEffect(() => {
        const normalized = String(initialFilter || 'all').toLowerCase();
        setActiveFilter(['all', 'ticket', 'customer', 'project', 'user'].includes(normalized) ? normalized : 'all');
    }, [initialFilter]);

    React.useEffect(() => {
        const normalizedQuery = String(query || '').trim();

        if (!normalizedQuery) {
            setResults(EMPTY_RESULTS);
            setLoading(false);
            setError(null);
            return undefined;
        }

        if (normalizedQuery.length < 2) {
            setResults(EMPTY_RESULTS);
            setLoading(false);
            setError(null);
            return undefined;
        }

        if (!token) {
            setResults(EMPTY_RESULTS);
            setLoading(false);
            setError('No se pudo validar la sesión.');
            return undefined;
        }

        let isCancelled = false;
        const timerId = window.setTimeout(async () => {
            setLoading(true);
            setError(null);

            const response = await globalSearch(token, {
                q: normalizedQuery,
                type: activeFilter,
                limit,
            });

            if (isCancelled) {
                return;
            }

            if (!response?.success) {
                setResults(EMPTY_RESULTS);
                setError(response?.message || 'No se pudo realizar la búsqueda.');
                setLoading(false);
                return;
            }

            setResults({
                tickets: Array.isArray(response?.data?.tickets) ? response.data.tickets : [],
                customers: Array.isArray(response?.data?.customers) ? response.data.customers : [],
                projects: Array.isArray(response?.data?.projects) ? response.data.projects : [],
                users: Array.isArray(response?.data?.users) ? response.data.users : [],
            });
            setLoading(false);
        }, debounceMs);

        return () => {
            isCancelled = true;
            window.clearTimeout(timerId);
        };
    }, [token, query, activeFilter, limit, debounceMs]);

    const totalCount = React.useMemo(() => {
        return [
            results.tickets,
            results.customers,
            results.projects,
            results.users,
        ].reduce((total, rows) => total + (Array.isArray(rows) ? rows.length : 0), 0);
    }, [results]);

    return {
        query,
        setQuery,
        activeFilter,
        setActiveFilter,
        results,
        loading,
        error,
        totalCount,
    };
};
